import React, { useEffect, useMemo, useState } from 'react';
import {
  Loader,
  RefreshCw,
  Search,
  AlertCircle,
  Copy,
  ExternalLink,
  Mail,
  Phone,
  ListChecks,
  Zap,
  Clock,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import {
  listLeads,
  refireEnrichChain,
  type LeadDTO,
} from '@/modules/leads-import';

interface LeadLandingsViewProps {
  addNotification?: (n: { type: string; title: string; message?: string }) => void;
}

const PUBLIC_SITES_URL =
  (import.meta as any).env?.VITE_PUBLIC_SITES_URL || 'https://www.brainstream.pro';

type LandingState = 'ready' | 'pending' | 'failed';

function fullLandingUrl(lead: LeadDTO): string {
  if (!lead.landing_path) return '';
  return `${PUBLIC_SITES_URL}${lead.landing_path}`;
}

function landingState(lead: LeadDTO): LandingState {
  if (lead.landing_path) return 'ready';
  // Sin slug = pending. La distinción de failed requiere endpoint específico
  // (próxima iteración). Por ahora todo lo que no tiene slug = pending.
  return 'pending';
}

export const LeadLandingsView: React.FC<LeadLandingsViewProps> = ({
  addNotification,
}) => {
  const [leads, setLeads] = useState<LeadDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'ready' | 'pending'>('all');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listLeads({ limit: 500 });
      setLeads(data);
    } catch (err: any) {
      setError(err?.message ?? 'Error cargando leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  const counts = useMemo(() => {
    const ready = leads.filter((l) => landingState(l) === 'ready').length;
    const pending = leads.filter((l) => landingState(l) === 'pending').length;
    return { total: leads.length, ready, pending };
  }, [leads]);

  const filtered = useMemo(() => {
    let out = leads;
    if (filter !== 'all') out = out.filter((l) => landingState(l) === filter);
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter((l) =>
        [l.name, l.email, l.phone, l.landing_slug]
          .map((s) => (s || '').toLowerCase())
          .some((s) => s.includes(q)),
      );
    }
    return out;
  }, [leads, filter, search]);

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      addNotification?.({ type: 'success', title: 'Copiado', message: label });
    } catch {
      addNotification?.({ type: 'error', title: 'No se pudo copiar' });
    }
  };

  const copyAllUrls = async () => {
    const lines = leads
      .filter((l) => l.landing_path)
      .map((l) => {
        const who = l.name || l.email || l.phone || '(sin nombre)';
        return `${who}\t${fullLandingUrl(l)}`;
      });
    if (!lines.length) {
      addNotification?.({ type: 'info', title: 'No hay landings generados aún' });
      return;
    }
    await copyToClipboard(lines.join('\n'), `${lines.length} URL(s) (TSV)`);
  };

  const generatePending = async () => {
    if (counts.pending === 0) {
      addNotification?.({
        type: 'info',
        title: 'Nada pendiente',
        message: 'Todos los leads ya tienen landing.',
      });
      return;
    }
    setDispatching(true);
    try {
      const r = await refireEnrichChain({ only_without_landing: true });
      addNotification?.({
        type: 'success',
        title: `${r.dispatched} chain(s) disparados`,
        message: `Workers procesando async. Refresca en 1-3 min.`,
      });
      // Auto-refresh cada 20s mientras hay pendientes
      const startTime = Date.now();
      const poll = async () => {
        if (Date.now() - startTime > 5 * 60 * 1000) return; // 5 min cap
        await fetchData();
        const stillPending = leads.filter((l) => landingState(l) === 'pending').length;
        if (stillPending > 0 && Date.now() - startTime < 5 * 60 * 1000) {
          setTimeout(poll, 20_000);
        }
      };
      setTimeout(poll, 20_000);
    } catch (e: any) {
      addNotification?.({
        type: 'error',
        title: 'Error disparando chain',
        message: e?.message,
      });
    } finally {
      setDispatching(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6 mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <ListChecks className="h-6 w-6 text-muted-foreground" />
              Landings & URLs
            </h2>
            <p className="text-muted-foreground text-sm mt-1">
              <span className="text-emerald-700 dark:text-emerald-300">{counts.ready} listos</span>
              {counts.pending > 0 && (
                <>
                  {' · '}
                  <span className="text-amber-700 dark:text-amber-300">{counts.pending} pendientes</span>
                </>
              )}
              {' · '}
              <span className="text-muted-foreground">{counts.total} leads totales</span>
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {counts.pending > 0 && (
              <button
                onClick={generatePending}
                disabled={dispatching}
                className="px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/30 rounded-lg text-amber-700 dark:text-amber-200 flex items-center gap-2 transition disabled:opacity-50"
              >
                {dispatching ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Zap className="w-4 h-4" />
                )}
                Generar {counts.pending} pendiente{counts.pending === 1 ? '' : 's'}
              </button>
            )}
            <button
              onClick={handleRefresh}
              disabled={refreshing || loading}
              className="px-4 py-2 bg-[var(--acc1)]/20 hover:bg-[var(--acc1)]/30 border border-[var(--acc1)]/30 rounded-lg text-[var(--acc1)] flex items-center gap-2 transition disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Actualizar
            </button>
            <button
              onClick={copyAllUrls}
              disabled={counts.ready === 0}
              className="px-4 py-2 bg-[var(--acc1)]/20 hover:bg-[var(--acc1)]/30 border border-[var(--acc1)]/30 rounded-lg text-[var(--acc1)] flex items-center gap-2 transition disabled:opacity-30"
              title={counts.ready === 0 ? 'No hay landings listos para copiar' : ''}
            >
              <Copy className="w-4 h-4" />
              Copiar todos (TSV)
            </button>
          </div>
        </div>

        {/* Filtros + search */}
        <div className="mt-5 flex flex-wrap gap-3 items-center">
          {(['all', 'ready', 'pending'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-full text-sm border transition ${
                filter === f
                  ? 'bg-[var(--acc1)]/30 border-[var(--acc1)] text-foreground'
                  : 'bg-muted/60 border-border text-muted-foreground hover:bg-muted'
              }`}
            >
              {f === 'all' ? 'Todos' : f === 'ready' ? '✓ Listos' : '⏳ Pendientes'}
            </button>
          ))}
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, email, teléfono o slug…"
              className="w-full bg-muted/50 border border-border text-foreground rounded-lg pl-9 pr-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[var(--acc1)]/50"
            />
          </div>
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <div className="flex flex-col items-center justify-center p-12 text-muted-foreground">
          <Loader className="animate-spin h-10 w-10 mb-4" />
          <span>Cargando leads…</span>
        </div>
      ) : error ? (
        <div className="bg-rose-500/10 dark:bg-rose-950/40 border border-rose-500/30 dark:border-rose-800/50 rounded-lg p-6 text-rose-700 dark:text-rose-200">
          <AlertCircle className="h-5 w-5 inline mr-2" />
          {error}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-8 text-center text-muted-foreground">
          <AlertCircle className="h-10 w-10 mx-auto mb-4" />
          <p className="text-lg font-medium">
            {leads.length === 0
              ? 'No hay leads en esta org todavía'
              : 'Ninguno coincide con los filtros'}
          </p>
          <p className="mt-2 text-sm">
            {leads.length === 0
              ? 'Sube un CSV en Subir CSV o crea un lead desde el agente.'
              : 'Ajusta los filtros o el search.'}
          </p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-4 py-3 text-left">Lead</th>
                  <th className="px-4 py-3 text-left">Contacto</th>
                  <th className="px-4 py-3 text-left">Estado</th>
                  <th className="px-4 py-3 text-left">URL pública</th>
                  <th className="px-4 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {filtered.map((l) => {
                  const state = landingState(l);
                  const url = fullLandingUrl(l);
                  return (
                    <tr key={l.id} className="hover:bg-muted/50 transition-colors">
                      <td className="px-4 py-3 align-top">
                        <div className="text-foreground font-medium">
                          {l.name || <span className="text-muted-foreground/70">Sin nombre</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top text-foreground/80">
                        {l.email && (
                          <div className="inline-flex items-center gap-1 text-xs">
                            <Mail className="h-3.5 w-3.5" /> {l.email}
                          </div>
                        )}
                        {l.phone && (
                          <div className="inline-flex items-center gap-1 text-xs text-foreground/70 mt-0.5">
                            <Phone className="h-3.5 w-3.5" /> {l.phone}
                          </div>
                        )}
                        {!l.email && !l.phone && (
                          <span className="text-muted-foreground/70 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <StateBadge state={state} />
                      </td>
                      <td className="px-4 py-3 align-top">
                        {url ? (
                          <code className="text-xs text-[var(--acc1)] break-all">{url}</code>
                        ) : (
                          <span className="text-muted-foreground/70 text-xs italic">
                            (se generará al disparar el chain)
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top text-right whitespace-nowrap">
                        {url && (
                          <div className="inline-flex gap-1">
                            <button
                              onClick={() => copyToClipboard(url, 'URL del landing')}
                              className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground"
                              title="Copiar URL"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground inline-flex"
                              title="Abrir en nueva pestaña"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const StateBadge: React.FC<{ state: LandingState }> = ({ state }) => {
  if (state === 'ready') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border bg-emerald-500/15 text-emerald-700 dark:text-emerald-200 border-emerald-500/30">
        <CheckCircle2 className="w-3 h-3" /> Listo
      </span>
    );
  }
  if (state === 'failed') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border bg-rose-500/15 text-rose-700 dark:text-rose-200 border-rose-500/30">
        <XCircle className="w-3 h-3" /> Falló
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border bg-amber-500/15 text-amber-700 dark:text-amber-200 border-amber-500/30">
      <Clock className="w-3 h-3" /> Pendiente
    </span>
  );
};
